import { TooltipProps } from "@blueprintjs/core";
import React from "react";
import styled, { css } from "styled-components";

import { HexTooltip } from "../../../hex-components";
import { HexToolTipProps } from "../../../hex-components/HexTooltip";
import { MinimalCategory } from "../../../hooks/useProjectLabelsForHex";

import { CategoryIcon } from "./OrganizationIcons";

// --- Provider ---

type ContextValue = {
  category: Omit<MinimalCategory, "id">;
};

const Context = React.createContext<ContextValue | null>(null);

const useContext = (): NonNullable<ContextValue> => {
  const context = React.useContext(Context);
  if (context == null) {
    throw new Error(
      "CategoryLabel must be used within a CategoryLabelProvider",
    );
  }
  return context;
};

export interface CategoryLabelProviderProps extends Omit<
  React.ComponentProps<typeof Context.Provider>,
  "value"
> {
  category: ContextValue["category"];
}

const Provider = React.memo(function Provider(
  props: CategoryLabelProviderProps,
) {
  const { category, ...rest } = props;
  const value = React.useMemo(() => ({ category }), [category]);
  return <Context.Provider value={value} {...rest} />;
});

// --- Label ---

const CategoryName = styled.div`
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
`;

export const CategoryLabelWrapper = styled.div<{
  $condensed?: boolean;
  $minimal?: boolean;
  $muted?: boolean;
}>`
  display: flex;
  align-items: center;
  min-width: 0;

  color: ${({ $muted, theme }) =>
    $muted ? theme.fontColor.MUTED : theme.fontColor.DEFAULT};

  ${({ $condensed }) =>
    $condensed
      ? css`
          height: 18px;
          font-size: ${({ theme }) => theme.fontSize.SMALL};
          padding: 2px;
          gap: 3px;

          ${CategoryIcon} {
            height: 12px;
            width: 5px;
          }
        `
      : css`
          height: 22px;
          font-size: ${({ theme }) => theme.fontSize.SMALL};
          line-height: 16px;
          padding: 4px 6px;
          gap: 4px;
        `}

  ${({ $condensed, $minimal }) =>
    $minimal &&
    css`
      justify-content: center;

      ${$condensed
        ? css`
            width: 10px;
          `
        : css`
            width: 22px;
          `}
    `};
`;

export interface CategoryLabelLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  category: ContextValue["category"];
  condensed?: boolean;
  minimal?: boolean;
  muted?: boolean;
}

const Label = React.memo(
  React.forwardRef<HTMLDivElement, CategoryLabelLabelProps>(
    function CategoryLabel(props, ref) {
      const { category, className, condensed, minimal, muted } = props;
      return (
        <CategoryLabelWrapper
          ref={ref}
          $condensed={condensed}
          $minimal={minimal}
          $muted={muted}
          className={className}
        >
          <CategoryIcon $color={category.color} />
          {!minimal && <CategoryName>{category.name}</CategoryName>}
        </CategoryLabelWrapper>
      );
    },
  ),
);

// --- Tooltip ---

export interface CategoryLabelTooltipProps extends Partial<HexToolTipProps> {}

const Tooltip = React.memo(function CategoryLabelTooltip(
  props: CategoryLabelTooltipProps,
) {
  const { category } = useContext();
  const hasDescription =
    category.description != null && category.description.trim().length > 0;
  const disabled = !hasDescription;
  return (
    <HexTooltip
      placement="bottom"
      {...props}
      content={category.description}
      disabled={disabled}
    />
  );
});

// --- Composition ---
export interface CategoryLabelProps {
  className?: string;
  category: Omit<MinimalCategory, "id">;
  /** Condensed form with smaller icon & less padding */
  condensed?: boolean;
  /** Icon-only form */
  minimal?: boolean;
  muted?: boolean;
  descriptionTooltipProps?: Omit<TooltipProps, "content">;
}

const Composition: React.ComponentType<CategoryLabelProps> = React.memo(
  function CategoryLabelComposition({
    category,
    className,
    condensed,
    descriptionTooltipProps,
    minimal,
    muted,
  }: CategoryLabelProps) {
    return (
      <Provider category={category}>
        <Tooltip {...descriptionTooltipProps}>
          <Label
            category={category}
            className={className}
            condensed={condensed}
            minimal={minimal}
            muted={muted}
          />
        </Tooltip>
      </Provider>
    );
  },
);

/**
 * @example
 * <CategoryLabel.Provider category>
 *   <CategoryLabel.Tooltip
 *     renderTarget={({ isOpen, ...tooltipTargetProps }) => (
 *       <CategoryLabel.Label muted {...tooltipTargetProps} />
 *     )}
 *   />
 * </CategoryLabel.Provider>
 */
export const CategoryLabel = Object.assign(Composition, {
  Tooltip,
  Provider,
  Label,
});
